import React, { useDeferredValue, useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Chip,
	InputAdornment,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TableSortLabel,
	TextField,
	Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { GlobalState } from "context/GlobalContext";
import { AuthState } from "context/AuthContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { canDownloadRefundTracker, refundTrackerRoleAccess } from "./data";

const formatCurrency = (value) => `₹ ${Number(value || 0).toLocaleString("en-IN")}`;

const toNumber = (value) => {
	const parsed = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
	return Number.isFinite(parsed) ? parsed : 0;
};

const toCsvValue = (value) => {
	if (value === null || value === undefined) return '""';
	const stringValue = typeof value === "object" ? JSON.stringify(value) : String(value);
	return `"${stringValue.replace(/"/g, '""')}"`;
};

const formatSingleDate = (value) => {
	if (value === null || value === undefined || value === "") return "";
	let text = String(value).trim();
	if (!text || text === "-") return "";

	text = text.replace(/\s+/g, " ").replace(/ UTC$/i, "Z");

	// Snapmint / DD/MM/YYYY (or D/M/YYYY)
	const dmy = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
	if (dmy) {
		const day = dmy[1].padStart(2, "0");
		const month = dmy[2].padStart(2, "0");
		const year = dmy[3];
		const parsed = dayjs(`${year}-${month}-${day}`);
		return parsed.isValid() ? parsed.format("DD MMM YYYY") : text;
	}

	// ISO / BigQuery timestamps: use calendar date part to avoid timezone day-shift
	const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (iso) {
		const parsed = dayjs(`${iso[1]}-${iso[2]}-${iso[3]}`);
		return parsed.isValid() ? parsed.format("DD MMM YYYY") : text;
	}

	// Fallback
	let parsed = dayjs(text);
	if (!parsed.isValid()) {
		parsed = dayjs(text.replace(/[+-]\d{2}:?\d{0,2}$/, "").trim());
	}
	return parsed.isValid() ? parsed.format("DD MMM YYYY") : text;
};

const formatSingleCsvDate = (value) => {
	if (value === null || value === undefined || value === "") return "";
	if (typeof value === "object") {
		value = value.value ?? value.date ?? value.datetime ?? JSON.stringify(value);
	}

	let text = String(value).trim();
	if (!text || text === "-" || text.toLowerCase() === "null" || text.toLowerCase() === "none") return "";

	text = text.replace(/\s+/g, " ").replace(/ UTC$/i, "Z");

	const dmy = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
	if (dmy) {
		const parsed = dayjs(`${dmy[3]}-${dmy[2].padStart(2, "0")}-${dmy[1].padStart(2, "0")}`);
		return parsed.isValid() ? parsed.format("DD/MM/YYYY") : text;
	}

	const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (iso) {
		const parsed = dayjs(`${iso[1]}-${iso[2]}-${iso[3]}`);
		return parsed.isValid() ? parsed.format("DD/MM/YYYY") : text;
	}

	let parsed = dayjs(text);
	if (!parsed.isValid()) {
		parsed = dayjs(text.replace(/[+-]\d{2}:?\d{0,2}$/, "").trim());
	}
	return parsed.isValid() ? parsed.format("DD/MM/YYYY") : text;
};

const formatDateValue = (value) => {
	if (value === null || value === undefined || value === "") return "-";

	// Keep comma-separated refund dates intact (e.g. "03/07/2026, 12/07/2026")
	const parts = String(value)
		.split(",")
		.map((part) => formatSingleDate(part.trim()))
		.filter(Boolean);

	return parts.length ? [...new Set(parts)].join(", ") : "-";
};

const formatCsvDateValue = (value) => {
	if (value === null || value === undefined || value === "") return "";

	const raw =
		typeof value === "object" ? value.value ?? value.date ?? value.datetime ?? "" : value;

	const parts = String(raw)
		.split(",")
		.map((part) => formatSingleCsvDate(part.trim()))
		.filter(Boolean);

	return parts.length ? [...new Set(parts)].join(", ") : "";
};

const splitMultiValues = (value) => {
	if (value === null || value === undefined || value === "") return [];
	return String(value)
		.split(",")
		.map((part) => part.trim())
		.filter((part) => part && part !== "-" && part.toLowerCase() !== "null" && part.toLowerCase() !== "none");
};

const toSafeText = (value) => {
	if (value === null || value === undefined || value === "") return "-";
	return String(value);
};

const isDateColumnKey = (key) =>
	/date|_at$/i.test(String(key || "")) || ["processed_at", "created_at", "refund_at"].includes(key);

const isMultiTextColumnKey = (key) =>
	["refund_id", "payment_id", "pay_id", "bank_arn", "bank_rrn", "partner_refund_id"].includes(key);

const getCsvHeaders = (rows = []) =>
	Array.from(
		rows.reduce((set, row) => {
			Object.keys(row || {}).forEach((key) => set.add(key));
			return set;
		}, new Set()),
	);

const formatCsvCellValue = (header, value) => {
	if (isDateColumnKey(header)) {
		return toCsvValue(formatCsvDateValue(value));
	}
	return toCsvValue(value);
};

const downloadRawCsv = (rows, fileName) => {
	if (!rows?.length) return;

	const headers = getCsvHeaders(rows);
	const csvRows = [
		headers.join(","),
		...rows.map((row) => headers.map((header) => formatCsvCellValue(header, row?.[header])).join(",")),
	];

	const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.setAttribute("download", fileName);
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};

const datePickerSx = {
	width: "100%",
	minWidth: 170,
	"& .MuiInputBase-root": { height: 40, borderRadius: 1.5 },
	"& .MuiInputBase-input": { fontSize: 13 },
};

const filterItemSx = {
	flex: { xs: "1 1 100%", sm: "1 1 170px" },
	minWidth: { xs: "100%", sm: 170 },
	maxWidth: { sm: 220 },
};

const PINNED_COLUMNS = [
	{ key: "order_name", label: "Order Name", width: 160 },
];

const SHOPIFY_COLUMNS = [
	{ key: "kind", label: "Kind", width: 90 },
	{ key: "gateway", label: "Gateway", width: 110 },
	{ key: "status", label: "Status", width: 90 },
	{ key: "amount", label: "Amount", width: 100, align: "right", type: "currency" },
	{ key: "processed_at", label: "Processed At", width: 150, type: "date" },
	{ key: "created_at", label: "Created At", width: 150, type: "date" },
	{ key: "payment_id", label: "Payment ID", width: 160, type: "multi" },
	{ key: "refund_id", label: "Refund ID", width: 170, type: "multi" },
];

const PARTNER_COLUMNS = [
	{ key: "partner_type", label: "Partner Type", width: 120 },
	{ key: "pay_id", label: "Pay ID", width: 160, type: "multi" },
	{ key: "partner_refund_id", label: "Partner Refund ID", width: 170, type: "multi" },
	{ key: "refund_amt", label: "Refund Amt", width: 110, align: "right", type: "currency" },
	{ key: "bank_arn", label: "Bank ARN", width: 150, type: "multi" },
	{ key: "bank_rrn", label: "Bank RRN", width: 150, type: "multi" },
	{ key: "refund_at", label: "Refund At", width: 150, type: "date" },
	{ key: "match_status", label: "Match", width: 110, align: "center" },
];

const COLUMNS = [...PINNED_COLUMNS, ...SHOPIFY_COLUMNS, ...PARTNER_COLUMNS];

const MONTH_NAME_TO_INDEX = {
	jan: 1,
	january: 1,
	feb: 2,
	february: 2,
	mar: 3,
	march: 3,
	apr: 4,
	april: 4,
	may: 5,
	jun: 6,
	june: 6,
	jul: 7,
	july: 7,
	aug: 8,
	august: 8,
	sep: 9,
	sept: 9,
	september: 9,
	oct: 10,
	october: 10,
	nov: 11,
	november: 11,
	dec: 12,
	december: 12,
};

const unwrapSortValue = (value) => {
	if (value === null || value === undefined) return "";
	if (typeof value === "object") {
		return value.value ?? value.date ?? value.datetime ?? value.timestamp ?? "";
	}
	return value;
};

const parseSingleDateToMs = (raw) => {
	if (raw === null || raw === undefined || raw === "") return null;
	let text = String(raw).trim();
	if (!text || text === "-" || text.toLowerCase() === "null" || text.toLowerCase() === "none") return null;

	text = text.replace(/\s+/g, " ").replace(/ UTC$/i, "Z");

	// DD/MM/YYYY or D/M/YYYY
	const dmy = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\b.*)?$/);
	if (dmy) {
		const parsed = dayjs(`${dmy[3]}-${dmy[2].padStart(2, "0")}-${dmy[1].padStart(2, "0")}`);
		return parsed.isValid() ? parsed.startOf("day").valueOf() : null;
	}

	// DD MMM YYYY / DD Month YYYY (display format in table)
	const dMonY = text.match(/^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})(?:\b.*)?$/);
	if (dMonY) {
		const month = MONTH_NAME_TO_INDEX[dMonY[2].toLowerCase()];
		if (month) {
			const parsed = dayjs(
				`${dMonY[3]}-${String(month).padStart(2, "0")}-${dMonY[1].padStart(2, "0")}`,
			);
			return parsed.isValid() ? parsed.startOf("day").valueOf() : null;
		}
	}

	// YYYY-MM-DD or ISO / BigQuery timestamps — use calendar date only
	const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (iso) {
		const parsed = dayjs(`${iso[1]}-${iso[2]}-${iso[3]}`);
		return parsed.isValid() ? parsed.startOf("day").valueOf() : null;
	}

	const cleaned = text.replace(/[+-]\d{2}:?\d{0,2}$/, "").trim();
	let parsed = dayjs(cleaned);
	if (!parsed.isValid()) parsed = dayjs(text);
	return parsed.isValid() ? parsed.startOf("day").valueOf() : null;
};

const parseDateFieldToMs = (value) => {
	const raw = unwrapSortValue(value);
	if (raw === null || raw === undefined || raw === "" || raw === "-") return null;

	const text = String(raw).trim();
	const parts = text.includes(",")
		? text
				.split(",")
				.map((part) => part.trim())
				.filter(Boolean)
		: [text];

	const timestamps = parts.map(parseSingleDateToMs).filter((ms) => ms !== null);
	if (!timestamps.length) return null;

	// Sort by earliest date in the cell for stable ordering.
	return Math.min(...timestamps);
};

const getSortComparableValue = (row, col) => {
	const value = row?.[col.key];
	if (value === null || value === undefined || value === "" || value === "-") return null;

	if (col.type === "currency") return toNumber(value);

	if (col.type === "date" || isDateColumnKey(col.key)) {
		return parseDateFieldToMs(value);
	}

	return String(unwrapSortValue(value)).toLowerCase();
};

const compareSortValues = (a, b, direction = "asc") => {
	if (a === null && b === null) return 0;
	if (a === null) return 1;
	if (b === null) return -1;

	let result = 0;
	if (typeof a === "number" && typeof b === "number") {
		result = a - b;
	} else {
		result = String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
	}
	return direction === "desc" ? -result : result;
};

const getPinnedLeft = (index) => PINNED_COLUMNS.slice(0, index).reduce((sum, col) => sum + col.width, 0);

const pinnedCellSx = (index, isHeader = false, rowBg = "#ffffff") => ({
	position: "sticky",
	left: getPinnedLeft(index),
	zIndex: isHeader ? 5 : 2,
	minWidth: PINNED_COLUMNS[index].width,
	width: PINNED_COLUMNS[index].width,
	maxWidth: PINNED_COLUMNS[index].width,
	backgroundColor: isHeader ? "#dbeafe" : rowBg,
	borderRight: index === PINNED_COLUMNS.length - 1 ? "2px solid #93c5fd" : "1px solid #bfdbfe",
	boxShadow: index === PINNED_COLUMNS.length - 1 ? "4px 0 8px -4px rgba(15, 23, 42, 0.12)" : "none",
	fontWeight: isHeader ? 700 : 400,
	fontSize: 12,
	py: 1.25,
	px: 1,
	verticalAlign: "middle",
	top: isHeader ? 0 : undefined,
});

const groupHeaderSx = (bg, color, borderColor) => ({
	backgroundColor: bg,
	color,
	fontWeight: 700,
	fontSize: 12,
	borderBottom: `1px solid ${borderColor}`,
	borderRight: `1px solid ${borderColor}`,
	py: 0.75,
	px: 1,
	textAlign: "center",
	top: 0,
	zIndex: 3,
});

const headerCellSx = (variant = "shopify") => {
	const styles =
		variant === "partner"
			? { backgroundColor: "#f5f3ff", borderBottom: "1px solid #ddd6fe", borderRight: "1px solid #ddd6fe" }
			: variant === "pinned"
				? { backgroundColor: "#dbeafe", borderBottom: "1px solid #bfdbfe", borderRight: "1px solid #bfdbfe" }
				: { backgroundColor: "#e0f2fe", borderBottom: "1px solid #7dd3fc", borderRight: "1px solid #7dd3fc" };

	return {
		...styles,
		fontWeight: 700,
		fontSize: 12,
		py: 1.25,
		px: 1,
		whiteSpace: "nowrap",
		top: 36,
	};
};

const bodyCellSx = (align = "left", variant = "default") => ({
	fontSize: 12,
	borderBottom: "1px solid #eceff1",
	borderRight: "1px solid #eceff1",
	py: 1,
	px: 1,
	textAlign: align,
	verticalAlign: "middle",
	backgroundColor: variant === "shopify" ? "#f8fbff" : variant === "partner" ? "#faf5ff" : undefined,
});

const ROLE_TAG_STYLES = {
	VIEWER: { backgroundColor: "#f1f5f9", color: "#475569" },
	EDITOR: { backgroundColor: "#fffbeb", color: "#b45309" },
	PUBLISHER: { backgroundColor: "#f5f3ff", color: "#7c3aed" },
	ADMIN: { backgroundColor: "#ecfdf5", color: "#047857" },
};

const GATEWAY_STYLES = {
	payu: { backgroundColor: "#dbeafe", color: "#1e40af" },
	razorpay: { backgroundColor: "#f5f3ff", color: "#7c3aed" },
	snapmint: { backgroundColor: "#ecfdf5", color: "#047857" },
};

const getMatchStatus = (row) => {
	const hasShopify = Boolean(row?.order_id || row?.payment_id);
	const hasPartner = Boolean(row?.pay_id || toNumber(row?.refund_amt));
	if (hasShopify && hasPartner) return "Matched";
	if (hasShopify) return "Shopify Only";
	if (hasPartner) return "Partner Only";
	return "Unknown";
};

const normalizeRows = (data = []) =>
	data.map((row, index) => {
		const order_id = toSafeText(row?.order_id);
		const order_name = toSafeText(row?.order_name ?? row?.orderName);
		const kind = toSafeText(row?.kind);
		const gateway = toSafeText(row?.gateway);
		const partner_type = toSafeText(row?.partner_type ?? row?.partnerType);
		const status = toSafeText(row?.status);
		const payment_id = toSafeText(row?.payment_id);
		const refund_id = toSafeText(row?.refund_id);
		const pay_id = toSafeText(row?.pay_id);
		const bank_arn = toSafeText(row?.bank_arn);
		const bank_rrn = toSafeText(row?.bank_rrn);
		const partner_refund_id = toSafeText(row?.partner_refund_id);
		const receipt = toSafeText(row?.receipt);
		const match_status = getMatchStatus(row);

		return {
			id: row?.id ?? `${order_id || "na"}-${payment_id || pay_id || index}-${index}`,
			order_id,
			order_name,
			kind,
			gateway,
			partner_type,
			status,
			amount: toNumber(row?.amount),
			processed_at: row?.processed_at,
			created_at: row?.created_at,
			payment_id,
			refund_id,
			pay_id,
			refund_amt: toNumber(row?.refund_amt),
			bank_arn,
			bank_rrn,
			refund_at: row?.refund_at,
			partner_refund_id,
			receipt,
			match_status,
			_searchHaystack: [
				order_id,
				order_name,
				kind,
				gateway,
				partner_type,
				status,
				payment_id,
				refund_id,
				pay_id,
				partner_refund_id,
				bank_arn,
				bank_rrn,
				receipt,
				match_status,
			]
				.join(" ")
				.toLowerCase(),
		};
	});

const renderGateway = (value) => {
	const key = String(value || "").toLowerCase();
	const style = Object.entries(GATEWAY_STYLES).find(([name]) => key.includes(name))?.[1] || {
		backgroundColor: "#f3f4f6",
		color: "#374151",
	};

	return (
		<Chip size="small" label={value || "-"} sx={{ fontWeight: 600, fontSize: 11, height: 24, ...style }} />
	);
};

const renderMatchStatus = (value) => {
	const styles = {
		Matched: { backgroundColor: "#ecfdf5", color: "#047857" },
		"Shopify Only": { backgroundColor: "#dbeafe", color: "#1e40af" },
		"Partner Only": { backgroundColor: "#fffbeb", color: "#b45309" },
		Unknown: { backgroundColor: "#f3f4f6", color: "#374151" },
	};
	const chipStyle = styles[value] || styles.Unknown;

	return (
		<Chip size="small" label={value} sx={{ fontWeight: 600, fontSize: 11, height: 24, ...chipStyle }} />
	);
};

const renderMultiValue = (value) => {
	const parts = splitMultiValues(value);
	if (!parts.length) return "-";
	const text = parts.join(", ");
	return (
		<Typography
			variant="body2"
			title={text}
			sx={{ fontSize: 12, color: "#334155", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.35 }}>
			{text}
		</Typography>
	);
};

const renderDateValue = (value) => {
	const formatted = formatDateValue(value);
	if (formatted === "-") return "-";
	return (
		<Typography
			variant="body2"
			title={formatted}
			sx={{ fontSize: 12, color: "#334155", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.35 }}>
			{formatted}
		</Typography>
	);
};

const renderCurrency = (value) => (
	<Typography variant="body2" fontWeight={600} color="#0f766e" sx={{ fontSize: 12 }}>
		{formatCurrency(value)}
	</Typography>
);

const renderCellValue = (row, col) => {
	if (col.type === "currency") return renderCurrency(row[col.key]);
	if (col.type === "date") return renderDateValue(row[col.key]);
	if (col.type === "multi" || isMultiTextColumnKey(col.key)) return renderMultiValue(row[col.key]);
	if (col.key === "gateway" || col.key === "partner_type") return renderGateway(row[col.key]);
	if (col.key === "match_status") return renderMatchStatus(row.match_status);
	return row[col.key] || "-";
};

const cellTextSx = {
	fontSize: 12,
	color: "#334155",
	whiteSpace: "normal",
	wordBreak: "break-word",
	lineHeight: 1.35,
};

const renderDataCellContent = (row, col) => {
	if (col.align === "right") {
		return <Box display="flex" justifyContent="flex-end">{renderCellValue(row, col)}</Box>;
	}
	if (col.align === "center") {
		return <Box display="flex" justifyContent="center">{renderCellValue(row, col)}</Box>;
	}
	if (
		col.type === "date" ||
		col.type === "multi" ||
		isMultiTextColumnKey(col.key) ||
		col.key === "gateway" ||
		col.key === "partner_type" ||
		col.key === "match_status"
	) {
		return renderCellValue(row, col);
	}
	return (
		<Typography
			variant="body2"
			title={String(row[col.key] ?? "")}
			sx={{ ...cellTextSx, fontWeight: col.key === "order_name" ? 600 : 400 }}>
			{row[col.key] || "-"}
		</Typography>
	);
};

const GATEWAYS = [
	{ key: "payu", label: "PayU", color: "#1e40af", bar: "#3b82f6", bg: "#eff6ff" },
	{ key: "razorpay", label: "Razorpay", color: "#7c3aed", bar: "#8b5cf6", bg: "#f5f3ff" },
	{ key: "snapmint", label: "Snapmint", color: "#047857", bar: "#10b981", bg: "#ecfdf5" },
	{ key: "other", label: "Other", color: "#64748b", bar: "#94a3b8", bg: "#f8fafc" },
];

const getGatewayKey = (row) => {
	const candidates = [row?.partner_type, row?.gateway, row?.partnerType]
		.map((value) => String(value ?? "").trim().toLowerCase())
		.filter((value) => value && value !== "-" && value !== "null" && value !== "undefined" && value !== "nan");

	for (const value of candidates) {
		if (value.includes("payu")) return "payu";
		if (value.includes("razorpay")) return "razorpay";
		if (value.includes("snapmint")) return "snapmint";
	}
	return "other";
};

const FilterPill = ({ label, active, onClick, activeSx }) => (
	<Chip
		label={label}
		size="small"
		onClick={onClick}
		sx={{
			fontWeight: 600,
			cursor: "pointer",
			...(active
				? activeSx
				: { backgroundColor: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0" }),
		}}
	/>
);

const InfoStat = ({ label, value, sub, color = "#1e293b" }) => (
	<Box sx={{ minWidth: 120 }}>
		<Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
			{label}
		</Typography>
		<Typography variant="subtitle1" fontWeight={700} sx={{ color, lineHeight: 1.2 }}>
			{value}
		</Typography>
		{sub && (
			<Typography variant="caption" color="text.secondary">
				{sub}
			</Typography>
		)}
	</Box>
);

const GatewaySplitCard = ({ label, color, barColor, bg, stats, total }) => {
	const count = stats?.count || 0;
	const amount = stats?.amount || 0;
	const matched = stats?.matched || 0;
	const shopifyOnly = stats?.shopifyOnly || 0;
	const partnerOnly = stats?.partnerOnly || 0;
	const gaps = shopifyOnly + partnerOnly;
	const pct = total ? Math.round((count / total) * 100) : 0;
	const matchPct = count ? Math.round((matched / count) * 100) : 0;

	return (
		<Box
			sx={{
				flex: 1,
				minWidth: 0,
				p: 1.5,
				borderRadius: 2,
				border: `1px solid ${barColor}55`,
				background: `linear-gradient(160deg, ${bg} 0%, #ffffff 72%)`,
			}}>
			<Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
				<Typography variant="body2" fontWeight={700} sx={{ color }}>
					{label}
				</Typography>
				<Chip
					size="small"
					label={`${pct}%`}
					sx={{ height: 22, fontWeight: 700, fontSize: 11, backgroundColor: `${barColor}22`, color }}
				/>
			</Stack>

			<Typography variant="h6" fontWeight={700} sx={{ color, lineHeight: 1.1, mb: 0.25 }}>
				{count}
			</Typography>
			<Typography variant="caption" color="text.secondary" display="block" mb={1}>
				{formatCurrency(amount)} total
			</Typography>

			<Box sx={{ height: 6, borderRadius: 99, backgroundColor: "#e2e8f0", overflow: "hidden", mb: 1.25 }}>
				<Box sx={{ width: `${pct}%`, height: "100%", backgroundColor: barColor, borderRadius: 99 }} />
			</Box>

			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr",
					gap: 0.75,
				}}>
				<Box>
					<Typography variant="caption" color="text.secondary" display="block">
						Matched
					</Typography>
					<Typography variant="body2" fontWeight={700} color="#047857">
						{matched}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{matchPct}% · {formatCurrency(stats?.matchedAmt || 0)}
					</Typography>
				</Box>
				<Box>
					<Typography variant="caption" color="text.secondary" display="block">
						Gaps
					</Typography>
					<Typography variant="body2" fontWeight={700} color="#b45309">
						{gaps}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{formatCurrency((stats?.shopifyOnlyAmt || 0) + (stats?.partnerOnlyAmt || 0))}
					</Typography>
				</Box>
			</Box>
		</Box>
	);
};

const emptyGatewayStats = () =>
	GATEWAYS.reduce((acc, g) => {
		acc[g.key] = {
			count: 0,
			amount: 0,
			matched: 0,
			matchedAmt: 0,
			shopifyOnly: 0,
			shopifyOnlyAmt: 0,
			partnerOnly: 0,
			partnerOnlyAmt: 0,
		};
		return acc;
	}, {});

export default function RefundTrackerDashboard() {
	const [rows, setRows] = useState([]);
	const [rawRows, setRawRows] = useState([]);
	const [search, setSearch] = useState("");
	const deferredSearch = useDeferredValue(search);
	const [matchFilter, setMatchFilter] = useState("all");
	const [sortBy, setSortBy] = useState("");
	const [sortDir, setSortDir] = useState("asc");
	const [fromDate, setFromDate] = useState(dayjs().startOf("month"));
	const [toDate, setToDate] = useState(dayjs());
	const [rangeError, setRangeError] = useState("");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(25);
	const isSearchPending = search !== deferredSearch;
	const { setLoadingOpen, setSnackBar, setSnackBarOpen } = GlobalState();
	const { user } = AuthState();
	const roleData = refundTrackerRoleAccess(user);
	const canDownload = canDownloadRefundTracker(roleData.roles);

	useEffect(() => {
		const fetchRefunds = async () => {
			if (!fromDate?.isValid() || !toDate?.isValid()) {
				setRows([]);
				setRawRows([]);
				return;
			}
			if (fromDate.isAfter(toDate, "day")) {
				setRangeError("From date must be earlier than or equal to To date");
				setRows([]);
				setRawRows([]);
				return;
			}

			setRangeError("");
			try {
				setLoadingOpen(true);
				const res = await pythonVmInstance({
					url: "/refund-tracker/dashboard",
					method: "GET",
					headers: { "Content-Type": "application/json" },
					params: {
						start_date: fromDate.format("YYYY-MM-DD"),
						end_date: toDate.format("YYYY-MM-DD"),
					},
				});
				const data = Array.isArray(res?.data) ? res.data : res?.data?.rows || res?.data?.data || [];
				setRawRows(data);
				setRows(normalizeRows(data));
			} catch {
				setRawRows([]);
				setRows([]);
				setSnackBarOpen(true);
				setSnackBar({
					message: "Failed to fetch refund tracker data.",
					severity: "warning",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
			} finally {
				setLoadingOpen(false);
			}
		};

		fetchRefunds();
	}, [fromDate, toDate, setLoadingOpen, setSnackBar, setSnackBarOpen]);

	useEffect(() => {
		setPage(0);
	}, [deferredSearch, matchFilter, sortBy, sortDir, fromDate, toDate, rows]);

	const searchFilteredRows = useMemo(() => {
		const query = deferredSearch.trim().toLowerCase();
		if (!query) return rows;
		return rows.filter((row) => (row._searchHaystack || "").includes(query));
	}, [rows, deferredSearch]);

	const filteredRows = useMemo(() => {
		if (matchFilter === "matched") {
			return searchFilteredRows.filter((row) => row.match_status === "Matched");
		}
		if (matchFilter === "mismatched") {
			return searchFilteredRows.filter((row) => row.match_status !== "Matched");
		}
		if (matchFilter === "shopify_only") {
			return searchFilteredRows.filter((row) => row.match_status === "Shopify Only");
		}
		if (matchFilter === "partner_only") {
			return searchFilteredRows.filter((row) => row.match_status === "Partner Only");
		}
		return searchFilteredRows;
	}, [searchFilteredRows, matchFilter]);

	const sortedRows = useMemo(() => {
		if (!sortBy) return filteredRows;
		const col = COLUMNS.find((item) => item.key === sortBy);
		if (!col) return filteredRows;

		return [...filteredRows].sort((left, right) =>
			compareSortValues(getSortComparableValue(left, col), getSortComparableValue(right, col), sortDir),
		);
	}, [filteredRows, sortBy, sortDir]);

	const paginatedRows = useMemo(() => {
		const start = page * rowsPerPage;
		return sortedRows.slice(start, start + rowsPerPage);
	}, [sortedRows, page, rowsPerPage]);

	const handleSort = (columnKey) => {
		if (sortBy === columnKey) {
			if (sortDir === "asc") {
				setSortDir("desc");
			} else {
				setSortBy("");
				setSortDir("asc");
			}
			return;
		}
		setSortBy(columnKey);
		setSortDir("asc");
	};

	const renderSortableHeader = (col, extraSx = {}) => (
		<TableCell
			key={col.key}
			align={col.align || "left"}
			sortDirection={sortBy === col.key ? sortDir : false}
			sx={{
				...extraSx,
				minWidth: col.width,
				width: col.width,
				cursor: "pointer",
				userSelect: "none",
			}}
			onClick={() => handleSort(col.key)}>
			<TableSortLabel
				active={sortBy === col.key}
				direction={sortBy === col.key ? sortDir : "asc"}
				sx={{
					width: "100%",
					justifyContent: col.align === "right" ? "flex-end" : col.align === "center" ? "center" : "flex-start",
					"& .MuiTableSortLabel-icon": { fontSize: 16 },
					color: "inherit !important",
					fontWeight: 700,
					fontSize: 12,
				}}>
				{col.label}
			</TableSortLabel>
		</TableCell>
	);

	const summary = useMemo(() => {
		return searchFilteredRows.reduce(
			(acc, row) => {
				const shopifyAmt = toNumber(row.amount);
				const partnerAmt = toNumber(row.refund_amt);
				const gateway = getGatewayKey(row);
				const rowAmt = partnerAmt || shopifyAmt;
				const gStats = acc.gatewayStats[gateway] || acc.gatewayStats.other;

				acc.shopifyAmount += shopifyAmt;
				acc.partnerAmount += partnerAmt;
				acc.total += 1;

				gStats.count += 1;
				gStats.amount += rowAmt;

				if (row.match_status === "Matched") {
					acc.matched += 1;
					gStats.matched += 1;
					gStats.matchedAmt += shopifyAmt || partnerAmt;
				} else if (row.match_status === "Shopify Only") {
					acc.shopifyOnly += 1;
					acc.shopifyOnlyAmt += shopifyAmt;
					acc.mismatched += 1;
					gStats.shopifyOnly += 1;
					gStats.shopifyOnlyAmt += shopifyAmt;
				} else if (row.match_status === "Partner Only") {
					acc.partnerOnly += 1;
					acc.partnerOnlyAmt += partnerAmt;
					acc.mismatched += 1;
					gStats.partnerOnly += 1;
					gStats.partnerOnlyAmt += partnerAmt;
				} else {
					acc.mismatched += 1;
				}

				return acc;
			},
			{
				shopifyAmount: 0,
				partnerAmount: 0,
				total: 0,
				matched: 0,
				mismatched: 0,
				shopifyOnly: 0,
				partnerOnly: 0,
				shopifyOnlyAmt: 0,
				partnerOnlyAmt: 0,
				gatewayStats: emptyGatewayStats(),
			},
		);
	}, [searchFilteredRows]);

	const handleMatchFilterClick = (filter) => {
		setMatchFilter((prev) => (prev === filter ? "all" : filter));
	};

	const handleDownloadRaw = () => {
		if (!canDownload) {
			setSnackBarOpen(true);
			setSnackBar({
				message: "You do not have permission to download this data.",
				severity: "warning",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		if (!rawRows.length) {
			setSnackBarOpen(true);
			setSnackBar({
				message: "No raw data available to download.",
				severity: "warning",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		const from = fromDate?.isValid() ? fromDate.format("YYYY-MM-DD") : "from";
		const to = toDate?.isValid() ? toDate.format("YYYY-MM-DD") : "to";
		downloadRawCsv(rawRows, `refund_tracker_raw_${from}_${to}.csv`);
	};

	const matchRate = summary.total ? ((summary.matched / summary.total) * 100).toFixed(1) : "0.0";
	const amountDiff = summary.shopifyAmount - summary.partnerAmount;
	const periodLabel =
		fromDate?.isValid() && toDate?.isValid()
			? `${fromDate.format("DD MMM YYYY")} – ${toDate.format("DD MMM YYYY")}`
			: "Select date range";

	return (
		<Box px={2} pb={3}>
			{/* Header */}
			<Stack
				direction={{ xs: "column", md: "row" }}
				justifyContent="space-between"
				alignItems={{ md: "flex-start" }}
				gap={1.5}
				mb={2}>
				<Box>
					<Typography variant="h6" fontWeight={700} color="#1e293b">
						Refund Tracker
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Period: {periodLabel} · {filteredRows.length} rows showing
					</Typography>
				</Box>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
						<Box sx={filterItemSx}>
							<DatePicker
								label="From"
								format="DD MMM YYYY"
								value={fromDate}
								maxDate={dayjs()}
								onChange={(value) => {
									const nextFrom = value || null;
									setFromDate(nextFrom);
									setToDate((prevTo) =>
										prevTo?.isValid() && nextFrom?.isValid() && prevTo.isBefore(nextFrom, "day")
											? null
											: prevTo,
									);
									setRows([]);
									setRawRows([]);
									setRangeError("");
									setMatchFilter("all");
								}}
								slotProps={{ textField: { size: "small", fullWidth: true, sx: datePickerSx } }}
							/>
						</Box>
						<Box sx={filterItemSx}>
							<DatePicker
								label="To"
								format="DD MMM YYYY"
								value={toDate}
								minDate={fromDate?.isValid() ? fromDate : undefined}
								maxDate={dayjs()}
								disabled={!fromDate?.isValid()}
								onChange={(value) => {
									setToDate(value || null);
									setMatchFilter("all");
								}}
								slotProps={{ textField: { size: "small", fullWidth: true, sx: datePickerSx } }}
							/>
						</Box>
						<Button
							variant="outlined"
							size="small"
							startIcon={<FileDownloadOutlinedIcon />}
							onClick={handleDownloadRaw}
							disabled={!canDownload || !rawRows.length || !!rangeError}
							sx={{ height: 40, px: 2, textTransform: "none", fontWeight: 600 }}>
							Export CSV
						</Button>
					</Stack>
				</LocalizationProvider>
			</Stack>
			{rangeError && (
				<Typography variant="caption" color="error" sx={{ mb: 1, display: "block" }}>
					{rangeError}
				</Typography>
			)}

			{/* KPI strip */}
			<Paper
				elevation={0}
				sx={{
					mb: 2,
					p: 2,
					borderRadius: 2,
					border: "1px solid #e5e7eb",
					display: "flex",
					flexWrap: "wrap",
					gap: 3,
					rowGap: 2,
				}}>
				<InfoStat label="Total Refunds" value={summary.total} sub="All gateways" />
				<InfoStat label="Match Rate" value={`${matchRate}%`} sub={`${summary.matched} matched`} color="#047857" />
				<InfoStat
					label="Shopify Amount"
					value={formatCurrency(summary.shopifyAmount)}
					sub="From Shopify"
					color="#1e40af"
				/>
				<InfoStat
					label="Partner Amount"
					value={formatCurrency(summary.partnerAmount)}
					sub="From partners"
					color="#7c3aed"
				/>
				<InfoStat
					label="Amount Diff"
					value={formatCurrency(amountDiff)}
					sub={amountDiff === 0 ? "Balanced" : amountDiff > 0 ? "Shopify higher" : "Partner higher"}
					color={amountDiff === 0 ? "#047857" : "#b45309"}
				/>
				<InfoStat
					label="Gaps"
					value={summary.mismatched}
					sub={`${summary.mismatched} mismatched`}
					color="#b45309"
				/>
			</Paper>

			{/* Detail panels */}
			<Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: "1px solid #e5e7eb" }}>
				<Typography variant="subtitle2" fontWeight={700} color="#1e293b" mb={1.5}>
					Gateway Split
				</Typography>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
						gap: 1.5,
					}}>
					{GATEWAYS.filter((g) => g.key !== "other").map((g) => (
						<GatewaySplitCard
							key={g.key}
							label={g.label}
							color={g.color}
							barColor={g.bar}
							bg={g.bg}
							stats={summary.gatewayStats?.[g.key]}
							total={summary.total}
						/>
					))}
				</Box>
				{(summary.gatewayStats?.other?.count || 0) > 0 && (
					<Typography variant="caption" color="text.secondary" sx={{ mt: 1.25, display: "block" }}>
						Other / unknown: {summary.gatewayStats.other.count} · {formatCurrency(summary.gatewayStats.other.amount)}
					</Typography>
				)}
			</Paper>

			{/* Table */}
			<Paper
				elevation={0}
				sx={{
					borderRadius: 2,
					overflow: "hidden",
					border: "1px solid #e5e7eb",
					boxShadow: "0px 1px 3px rgba(0,0,0,0.08)",
					width: "100%",
				}}>
				<Box
					sx={{
						px: 2,
						py: 1.5,
						borderBottom: "1px solid #e5e7eb",
						backgroundColor: "#f8fafc",
						display: "flex",
						flexDirection: { xs: "column", md: "row" },
						gap: 1.5,
						alignItems: { md: "center" },
						justifyContent: "space-between",
					}}>
					<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
						<FilterPill
							label={`All (${summary.total})`}
							active={matchFilter === "all"}
							onClick={() => setMatchFilter("all")}
							activeSx={{ backgroundColor: "#dbeafe", color: "#1e40af" }}
						/>
						<FilterPill
							label={`Matched (${summary.matched})`}
							active={matchFilter === "matched"}
							onClick={() => handleMatchFilterClick("matched")}
							activeSx={{ backgroundColor: "#ecfdf5", color: "#047857" }}
						/>
						<FilterPill
							label={`Mismatched (${summary.mismatched})`}
							active={matchFilter === "mismatched"}
							onClick={() => handleMatchFilterClick("mismatched")}
							activeSx={{ backgroundColor: "#fffbeb", color: "#b45309" }}
						/>
					</Stack>
					<TextField
						size="small"
						placeholder="Search order, partner, payment id, ARN..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						sx={{ width: { xs: "100%", md: 300 } }}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" color="action" />
								</InputAdornment>
							),
						}}
					/>
				</Box>

				<TableContainer sx={{ maxHeight: 520, opacity: isSearchPending ? 0.7 : 1, transition: "opacity 120ms ease" }}>
					<Table stickyHeader size="small" sx={{ minWidth: 2000, tableLayout: "fixed" }}>
						<TableHead>
							<TableRow>
								<TableCell
									colSpan={PINNED_COLUMNS.length}
									sx={{
										...groupHeaderSx("#dbeafe", "#1e40af", "#93c5fd"),
										position: "sticky",
										left: 0,
										zIndex: 6,
										minWidth: PINNED_COLUMNS.reduce((sum, col) => sum + col.width, 0),
										width: PINNED_COLUMNS.reduce((sum, col) => sum + col.width, 0),
										boxShadow: "4px 0 8px -4px rgba(15, 23, 42, 0.12)",
										borderRight: "2px solid #93c5fd",
									}}>
									Order
								</TableCell>
								<TableCell colSpan={SHOPIFY_COLUMNS.length} sx={groupHeaderSx("#e0f2fe", "#0369a1", "#7dd3fc")}>
									Shopify
								</TableCell>
								<TableCell colSpan={PARTNER_COLUMNS.length} sx={groupHeaderSx("#f5f3ff", "#7c3aed", "#ddd6fe")}>
									Partner
								</TableCell>
							</TableRow>
							<TableRow>
								{PINNED_COLUMNS.map((col, index) =>
									renderSortableHeader(col, {
										...headerCellSx("pinned"),
										...pinnedCellSx(index, true),
										top: 36,
										zIndex: 5,
										textAlign: col.align || "left",
									}),
								)}
								{SHOPIFY_COLUMNS.map((col) =>
									renderSortableHeader(col, {
										...headerCellSx("shopify"),
										top: 36,
										zIndex: 4,
									}),
								)}
								{PARTNER_COLUMNS.map((col) =>
									renderSortableHeader(col, {
										...headerCellSx("partner"),
										top: 36,
										zIndex: 4,
									}),
								)}
							</TableRow>
						</TableHead>
						<TableBody>
							{paginatedRows.map((row, index) => {
								const rowBg = index % 2 === 0 ? "#ffffff" : "#f8fafc";
								return (
									<TableRow
										key={row.id}
										hover
										sx={{
											backgroundColor: rowBg,
											"&:hover": { backgroundColor: "#eff6ff" },
											"&:hover .pinned-cell": { backgroundColor: "#eff6ff" },
										}}>
										{PINNED_COLUMNS.map((col, pinnedIndex) => (
											<TableCell
												key={`${row.id}-${col.key}`}
												align={col.align || "left"}
												className="pinned-cell"
												sx={{
													...pinnedCellSx(pinnedIndex, false, rowBg),
													textAlign: col.align || "left",
												}}>
												{renderDataCellContent(row, col)}
											</TableCell>
										))}
										{SHOPIFY_COLUMNS.map((col) => (
											<TableCell
												key={`${row.id}-${col.key}`}
												align={col.align || "left"}
												sx={bodyCellSx(col.align, "shopify")}>
												{renderDataCellContent(row, col)}
											</TableCell>
										))}
										{PARTNER_COLUMNS.map((col) => (
											<TableCell
												key={`${row.id}-${col.key}`}
												align={col.align || "left"}
												sx={bodyCellSx(col.align, "partner")}>
												{renderDataCellContent(row, col)}
											</TableCell>
										))}
									</TableRow>
								);
							})}
							{!filteredRows.length && (
								<TableRow>
									<TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 4 }}>
										<Typography color="text.secondary">No refund records found</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					component="div"
					count={filteredRows.length}
					page={page}
					onPageChange={(_, newPage) => setPage(newPage)}
					rowsPerPage={rowsPerPage}
					onRowsPerPageChange={(event) => {
						setRowsPerPage(parseInt(event.target.value, 10));
						setPage(0);
					}}
					rowsPerPageOptions={[10, 25, 50, 100]}
					labelRowsPerPage="Rows per page"
					sx={{
						borderTop: "1px solid #dbeafe",
						backgroundColor: "#f8fafc",
						"& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
							fontSize: 13,
						},
					}}
				/>
			</Paper>
		</Box>
	);
}
